import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ПОЗЕМКА И ВЬЮГА — эффект зимних подложек (winter_1..winter_5).
 *
 * ПОЗЕМКА. Ветер несет снег над самой землей: по снежной равнине то и дело
 * проходит порыв — полоса из двух с лишним десятков тонких извилистых лент
 * снега, а между порывами тянутся редкие одиночные ленты (см.
 * utils/effects/biome/snowDrift). Лента — не прямой штрих, а цепочка коротких
 * тонких штрихов, уложенных внахлест вдоль извилистого русла: она течет по
 * нему, как дымок по неровной земле. Так же устроены ленты песка в пустыне
 * (sandStreamers), только снежные тоньше, короче и их втрое больше. Ленты
 * одного порыва изгибаются согласно, как струи одного потока, и друг друга
 * не пересекают.
 *
 * Поземка стелется ПО ЖЕТОНАМ СТРОЕНИЙ И ГОР, но под отрядами и их значками
 * (слой LOW_AIR): отряды и шевроны остаются над снегом. Так решил владелец
 * игры: прежняя поземка из прямых расплывчатых штрихов под жетонами на
 * поземку похожа не была, а под горами ленты обрывались у каждой вершины.
 *
 * ВЬЮГА. Время от времени на поле налетает снежный заряд — настоящая
 * непогода на полминуты: над всем полем по ветру летит густой снег, за
 * горами его закручивает вихрями, поле выбеливает пелена, по ней плывут клубы
 * мглы погуще (см. utils/effects/biome/snowSquall). Основная масса снега,
 * пелена и мгла идут над строениями и горами, но под отрядами — отряды и их
 * значки в бурю читаются; поверх отрядов летит только редкий снег.
 *
 * У ВЬЮГИ ЕСТЬ ЗВУК — вой ветра (SNOW_SQUALL_SOUND_ID, отложенный звук, см.
 * configs/deferredSounds: качается в фоне и только на зимних картах). Заряд
 * не начинается, пока звук не загружен: картинка и вой идут только вместе, и
 * оба плавно нарастают и плавно стихают (scenes/FieldAmbience).
 *
 * Идет все это тем же ветром, что и облака.
 */
export const SNOW_DRIFT_KIND = 'snowDrift';

/** Вой вьюги: имя отложенного звука (configs/deferredSounds) */
export const SNOW_SQUALL_SOUND_ID = 'storm_icy_wind';

const SNOW_DRIFT = Object.freeze({
  // Порывы поземки
  gusts: {
    // Первый — почти сразу, следующий — через паузу от начала предыдущего.
    // Порыв идет через поле 8–15 секунд (обычно 9–13): новый приходит сразу
    // за ушедшим или после нескольких секунд тишины, двух порывов разом не
    // бывает
    firstDelayMs: {min: 600, max: 2000},
    intervalMs: {min: 8000, max: 13000},
    // Лент в порыве — втрое больше, чем в пустыне (там 6–8); потолок поземки —
    // 28 лент порыва и 4 одиночные по 8 штрихов, 256 спрайтов
    count: {min: 24, max: 28},
    // Расстояние между соседними лентами поперек ветра, размеров ячейки:
    // порыв выходит полосой в 14–22 размера ячейки (7–11 ячеек в ширину)
    laneGapFactor: {min: 0.45, max: 1},
    // На сколько лента отстает от фронта порыва, размеров ячейки: ленты идут
    // не шеренгой, а россыпью
    lagFactor: {min: 0, max: 14},
    // Сколько порыв проходит (размеров ячейки) и с какой скоростью (в секунду)
    travelFactor: {min: 20, max: 26},
    speedFactor: {min: 3.4, max: 4.6},
    // В начале и в конце пути ленты проступают и тают на таком отрезке
    fadeFactor: 4,
  },
  // Одиночные ленты между порывами: поле не замирает совсем. Каждая рождается
  // в свой черед, разом — не больше четырех; две — с самого начала
  lone: {
    firstDelayMs: {min: 200, max: 900},
    intervalMs: {min: 1000, max: 2000},
    maxGroups: 4,
    prewarm: 2,
    travelFactor: {min: 9, max: 14},
    speedFactor: {min: 2.6, max: 3.8},
    fadeFactor: 3,
  },
  // Лента снега
  ribbon: {
    // Лента собрана из коротких штрихов, уложенных вдоль русла внахлест: кадр
    // штриха длиннее шага между штрихами во столько раз — яркость вдоль ленты
    // ровная, без «бусин»
    // Штрихов много и они короткие: от штриха к штриху лента поворачивает на
    // считаные градусы и читается плавной линией, а не пучком прямых иголок
    segmentCount: 8,
    segmentOverlap: 2.4,
    // Длина ленты, размеров ячейки
    lengthFactor: {min: 2.4, max: 4.4},
    // Высота кадра штриха у головы ленты, размеров ячейки. В кадре — белый
    // гребень (шестая часть высоты кадра: волосок в двадцатую долю ячейки) и
    // серо-голубая тень под ним. К хвосту лента еще тоньше
    thicknessFactor: {min: 0.3, max: 0.42},
    tailThicknessShare: 0.55,
    // Русло: две волны поперек пути, неподвижные в пространстве, — лента по
    // ним скользит и изгибается. Размах и длина волны — в размерах ячейки.
    // Длина и место главной волны у лент одного порыва общие (ленты идут как
    // струи одного потока и не пересекаются), размах — у каждой свой: доля
    // общего. Наклон русла к ветру при этих числах — не круче 42 градусов
    // (обычно до 25)
    waveAmplitudeFactor: {min: 0.35, max: 0.6},
    waveLengthFactor: {min: 6.5, max: 10},
    waveAmplitudeShare: {min: 0.8, max: 1.2},
    // Гребни волн у соседних лент чуть сдвинуты: на столько радиан на размер
    // ячейки поперек ветра — фронт волны идет наискось
    wavePhaseShear: {min: -0.25, max: 0.25},
    // Вторая волна — своя у каждой ленты, мелкая и почти такой же длины
    // (доли первой): изгиб выходит неровным, но без изломов
    rippleAmplitudeShare: {min: 0.1, max: 0.2},
    rippleLengthShare: {min: 0.7, max: 0.9},
    // Лента чуть быстрее или медленнее порыва
    speedShare: {min: 0.85, max: 1.15},
    // Одни ленты плотнее, другие бледнее (доля плотности подложки)
    alphaShare: {min: 0.7, max: 1},
    // Плотность вдоль ленты: к обоим концам сходит на нет (степень синуса),
    // хвост бледнее головы (доля плотности у хвоста)
    taperPower: 0.7,
    tailShare: 0.45,
    // Лента дышит: то плотнее, то бледнее на такую долю
    pulseDepth: 0.35,
    pulsePeriodMs: {min: 1800, max: 3200},
  },

  // Вьюга: снежный заряд
  squall: {
    // Первый заряд — через полминуты-минуту после начала, следующий — через
    // паузу от начала предыдущего: между зарядами минута-полторы затишья
    firstDelayMs: {min: 25000, max: 50000},
    intervalMs: {min: 85000, max: 130000},
    // Заряд идет столько; плавно налетает и плавно стихает за такие доли
    // срока (семь-девять секунд) — так же нарастает и затихает его вой
    durationMs: {min: 28000, max: 36000},
    riseShare: 0.25,
    fallShare: 0.3,
    // Вьюга идет волнами: плотность снега и мглы то и дело проседает на
    // такую долю
    waveDepth: 0.3,
    wavePeriodMs: {min: 5000, max: 8000},
    // Область заряда — все поле с таким запасом вокруг (размеров ячейки),
    // куда бы ни смотрела камера: буря не кончается за краем кадра
    fieldMarginFactor: 4,
    // Только на огромном поле («Европа»), где роев нужно во столько раз
    // больше потолка, заряд идет в окне вокруг камеры — то, что она видит в
    // миг его начала, с таким запасом (размеров ячейки): иначе вьюги там
    // не было бы вовсе
    narrowWhenOver: 3,
    viewMarginFactor: 10,
    // Рои снежинок под отрядами: основная масса вьюги. Рой — кадр с россыпью
    // мелких округлых хлопьев (текстура FLURRY)
    flakes: {
      // Сколько раз снежинки роев покрывают круг их пути через область заряда
      // (рои лежат внахлест и разложены ровно) и сколько роев самое большее.
      // Потолка хватает на обычные поля; на самых больших вьюга реже заданного
      coverage: 1.1,
      maxCount: 120,
      // Кадр роя, размеров ячейки
      sizeFactor: {min: 4.4, max: 5.6},
      // Скорость, размеров ячейки в секунду: снег летит чуть быстрее поземки
      // (владелец игры: быстрее — уже не снег, а помехи). У роев одного слоя
      // она почти одна: пока заряд налетает (до 9 секунд), самый быстрый рой
      // уходит от самого медленного меньше чем на свой кадр — ровная
      // раскладка по области к разгару заряда еще не сбилась в комки
      speedFactor: {min: 4.8, max: 5.2},
      alpha: {min: 0.75, max: 1},
      // Рой покачивается поперек ветра. Поворачиваться ему нельзя — ни в
      // вихре, ни самому по себе, даже медленно: в кадре роя полторы сотни
      // хлопьев, и его поворот виден как поворот всей россыпи разом
      // (замечание владельца игры)
      swayFactor: {min: 0.3, max: 0.8},
      swayPeriodMs: {min: 2600, max: 4200},
    },
    // Редкий снег поверх отрядов: те же рои, но их мало и они бледнее
    top: {
      coverage: 0.14,
      maxCount: 16,
      sizeFactor: {min: 5, max: 6.2},
      speedFactor: {min: 6, max: 6.4},
      alpha: {min: 0.4, max: 0.55},
      swayFactor: {min: 0.3, max: 0.8},
      swayPeriodMs: {min: 2600, max: 4200},
    },
    // Завихрения: отдельные снежинки (по одной на спрайт), которые несет
    // струями и закручивает небольшими вихрями. Вихри стоят за горами и плывут
    // над чистым полем; рои снега летят сквозь них прямо
    eddies: {
      // Радиус вихря, размеров ячейки: вихрь — с ячейку-полторы в ширину
      radiusFactor: {min: 1.1, max: 1.5},
      // Вихри за горами: по два на гору, закручены навстречу друг другу
      lee: {
        // Скольким горам достаются вихри: на все горы поля спрайтов не хватит.
        // Такая доля — горам в кадре камеры в миг рождения заряда (и на
        // столько размеров ячейки вокруг кадра), остальные — по всему полю:
        // камеру за полминуты успеют передвинуть
        maxMountains: 4,
        nearShare: 0.75,
        viewMarginFactor: 2,
        // Ближе стольких размеров ячейки друг к другу горы вихрей разом не
        // получают: у соседних гор гряды вихри легли бы друг на друга
        gapFactor: 3.5,
        // Середина вихря — на столько размеров ячейки за горой по ветру и на
        // столько в сторону от нее: вихри начинаются у самой горы и за ней
        // сходятся
        leeFactor: 1.5,
        sideFactor: 0.85,
        // Через вихрь идет одна струя: за горой вихрей пара, и вдвоем они
        // читаются. Снежинок в струе — столько; в вихре разом — пять-шесть,
        // остальные на подходе к нему и на выходе
        streamCount: 1,
        flakeCount: 8,
        // Дорожка струи идет мимо середины вихря на таком расстоянии (доля
        // его радиуса): ближе — виток туже
        laneShare: {min: 0.3, max: 0.6},
        /**
         * ВСПЫШКИ. Вихри за горой не крутятся всю вьюгу: владелец игры
         * увидел, что они «перманентно и равномерно все время бури крутятся»
         * на одном месте, и это выглядит странно. Пара вихрей горы плавно
         * проступает, делает один-три оборота и плавно тает — глаз не
         * успевает за нее зацепиться; потом пауза до следующей вспышки.
         * Обе стороны пары вспыхивают вместе, горы — вразнобой.
         */
        burst: {
          // Сколько оборотов длится вспышка вместе с проступанием и таянием.
          // Оборот — время, за которое снежинка проходит вихрь (секунда с
          // небольшим). Сколько именно — у каждой вспышки свое
          turns: {min: 1, max: 3},
          // Доли срока вспышки на проступание и на таяние
          fadeInShare: 0.3,
          fadeOutShare: 0.35,
          // У горы одна вспышка за такой срок, мс, и в свой миг этого срока:
          // вспышки идут не по метроному. За заряд гора вспыхивает три-пять раз
          cycleMs: {min: 6000, max: 10000},
          // Вспышка — не длиннее такой доли своего срока: остальное — пауза
          // между вспышками (не короче двух-трех секунд)
          maxCycleShare: 0.7,
        },
      },
      // Вольные вихри: плывут по ветру над всей областью заряда
      free: {
        // Сколько их; такая доля — в полосе, которую камера видит в миг
        // рождения заряда, остальные — по всей ширине области. Вихрь плывет
        // через всю область, и в кадре компьютера (он видит ее малую часть)
        // вихрь из «своей» полосы бывает половину времени: при трех таких
        // в кадре обычно есть хоть один
        count: 5,
        nearShare: 0.6,
        // Вихрь плывет по ветру медленнее потока (доля его скорости): снег
        // идет сквозь него
        driftShare: {min: 0.3, max: 0.45},
        // Вольный вихрь — один посреди поля: струй через него две, на
        // разных расстояниях от середины, — внутренняя кружит быстрее внешней
        streamCount: 2,
        flakeCount: 7,
        laneShare: {min: 0.18, max: 0.6},
      },
      // Струя снежинок через вихрь
      stream: {
        // Сколько оборотов вихрь добавляет снежинке. Только целое число: на
        // выходе она снова на своей дорожке. Вместе с тем, что она огибает
        // середину вихря и на прямом пути, выходит виток с третью
        turns: 1,
        // До вихря и после него — по столько размеров ячейки пути: на них
        // снежинка проступает и тает
        legFactor: 1,
        // В середине вихря затишье: снежинка идет вдоль витка с такой долей
        // скорости потока (у кромки вихря — с полной). Быстрее потока она не
        // летит нигде: быстрый снег — уже помехи (владелец игры)
        coreSpeedShare: 0.7,
        // Снежинки идут друг за другом не ровно (сдвиг — доля промежутка) и
        // не по нитке (каждая — на столько размеров ячейки в стороне от
        // общего пути)
        gapJitter: {min: -0.3, max: 0.3},
        scatterFactor: {min: -0.1, max: 0.1},
        // Каждая такая по счету снежинка летит поверх отрядов
        aboveUnitsEvery: 5,
      },
      // Снежинка: тот же хлопок, что в рое, и на экране такого же размера
      flake: {
        // Кадр снежинки, размеров ячейки (сам хлопок — 0.72 кадра); в длину
        // хлопок больше, чем в ширину, во столько раз
        sizeFactor: {min: 0.16, max: 0.26},
        stretch: {min: 1, max: 1.4},
        alpha: {min: 0.8, max: 1},
        // Снежинки поверх отрядов бледнее — как рои редкого снега
        aboveUnitsAlphaShare: 0.55,
      },
    },
    // Пелена: одно большое мягкое пятно на всю область заряда, ровно
    // выбеливает жетоны строений и гор, пока идет снег (на белом снегу ее
    // саму не видно). Кадр пятна — во столько раз больше области: плотная
    // середина пятна накрывает ее целиком
    veil: {
      alpha: 0.16,
      sizeShare: 1.6,
    },
    // Мгла: большие мягкие клубы плывут медленнее снега — местами поле
    // затягивает плотнее. Клубы закрывают около трети поля
    haze: {
      // Сколько раз плотная середина клубов (около четверти кадра) покрывает
      // круг их пути и сколько клубов самое большее
      coverage: 0.35,
      maxCount: 12,
      sizeFactor: {min: 12, max: 18},
      speedFactor: {min: 2, max: 2.8},
      alpha: {min: 0.25, max: 0.38},
      spinDegPerSecond: 4,
    },
    // К краям области заряда снег и мгла сходят на нет: степень синуса по
    // пути через область
    edgePower: 0.6,
  },
});

/**
 * По подложкам:
 *  - snow — чистый снег на картинке пейзажа (доли ее стороны): до cleanRadius
 *    от центра ленты видны в полную силу, к fadeRadius сходят на нет. Дальше —
 *    леса, реки, скалы и море: белая лента на них читалась бы меловой чертой;
 *  - white — краска и плотность ленты (одного штриха; лента из штрихов
 *    внахлест в самом плотном месте плотнее примерно в 1.4 раза). Краска
 *    ложится и на гребень, и на тень штриха;
 *  - squallTint — краска снега и мглы вьюги.
 */
export const SNOW_DRIFT_EFFECT = Object.freeze({
  kind: SNOW_DRIFT_KIND,
  backgrounds: Object.freeze({
    // Самый светлый снег из пяти: плотность наибольшая
    [GROUND_BACKGROUNDS.WINTER_1]: Object.freeze({
      ...SNOW_DRIFT,
      snow: {u: 0.5, v: 0.5, cleanRadius: 0.26, fadeRadius: 0.32},
      white: {tint: 0xffffff, alpha: 0.9},
      squallTint: 0xffffff,
    }),
    [GROUND_BACKGROUNDS.WINTER_2]: Object.freeze({
      ...SNOW_DRIFT,
      snow: {u: 0.5, v: 0.5, cleanRadius: 0.28, fadeRadius: 0.34},
      white: {tint: 0xffffff, alpha: 0.85},
      squallTint: 0xffffff,
    }),
    // Самая маленькая равнина: по краям поля уже реки, скалы и опушки
    [GROUND_BACKGROUNDS.WINTER_3]: Object.freeze({
      ...SNOW_DRIFT,
      snow: {u: 0.5, v: 0.5, cleanRadius: 0.22, fadeRadius: 0.29},
      white: {tint: 0xffffff, alpha: 0.85},
      squallTint: 0xffffff,
    }),
    // Ледник
    [GROUND_BACKGROUNDS.WINTER_4]: Object.freeze({
      ...SNOW_DRIFT,
      snow: {u: 0.5, v: 0.5, cleanRadius: 0.25, fadeRadius: 0.31},
      white: {tint: 0xffffff, alpha: 0.85},
      squallTint: 0xffffff,
    }),
    // Ровный серо-бирюзовый лед озера: на нем видна любая лента, плотность
    // наименьшая, краска чуть холоднее
    [GROUND_BACKGROUNDS.WINTER_5]: Object.freeze({
      ...SNOW_DRIFT,
      snow: {u: 0.5, v: 0.5, cleanRadius: 0.3, fadeRadius: 0.36},
      white: {tint: 0xf4fbfb, alpha: 0.75},
      squallTint: 0xf4fbfb,
    }),
  }),
});
